import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { pluralize } from '../../../utils/language';
import { ControlledAlertModal } from '../../common/ControlledModal';
import { ScrollableWithGradient } from '../../common/ui/ScrollableWithGradient';
import { FileIcon, FolderIcon } from '../FileExplorerIcons';
import { DraggedItem } from '../hooks/useFileDragAndDrop';
import { moveItems, TreeFolder } from '../services';
import { folderKeys } from '../utils/queryKeys';

interface MoveItemsModalProps {
  show: boolean;
  onHide: () => void;
  items: DraggedItem[];
  targetFolder: TreeFolder | null;
}

export function MoveItemsModal({
  show,
  onHide,
  items,
  targetFolder,
}: MoveItemsModalProps) {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: () => moveItems({
      folder_id: targetFolder!.id,
      files: items
        .filter((item) => item.type === 'file')
        .map((item) => item.id),
      folders: items
        .filter((item) => item.type === 'folder')
        .map((item) => item.id),
    }),
    onSuccess: async () => {
      onHide();
      await queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });

  if (items.length === 0 || !targetFolder) return null;

  const handleMove = () => {
    const movePromise = mutateAsync();
    toast.promise(movePromise, {
      loading: 'Moviendo elementos...',
      success: 'Elementos movidos exitosamente',
      error: {
        message: 'Error al mover elementos',
        description:
          'Ocurrió un error al mover los elementos, por favor intenta nuevamente.',
      },
    });
  };

  return (
    <ControlledAlertModal
      show={show}
      onHide={onHide}
      title="Mover elementos"
      description={`Vas a mover ${items.length} ${pluralize(items.length, 'elemento', 'elementos')} hacia la carpeta ${targetFolder.name}.`}
      closeButton
      centered
    >
      <Modal.Body>
        <ScrollableWithGradient
          maxHeight="min(500px,80vh)"
          gradientHeight={130}
        >
          <ul className="list-unstyled d-flex flex-column gap-2">
            {items.map((item) => (
              <li className="p-2 bg-white border border-gray-300  rounded d-flex flex-row gap-2 align-items-center">
                {item.type === 'folder' ? <FolderIcon /> : <FileIcon />}
                {item.name}
              </li>
            ))}
          </ul>
        </ScrollableWithGradient>
      </Modal.Body>
      <Modal.Footer>
        <ControlledAlertModal.Actions
          handleClose={onHide}
          onConfirm={handleMove}
          isPending={isLoading}
        />
      </Modal.Footer>
    </ControlledAlertModal>
  );
}
